"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Box } from '../Box/Box.web.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { IconCross } from '../icons/ui_control/IconCross.web.js';
import { ContentText } from '../ContentText/ContentText.web.js';
import { outerContainer, contentWrapper, textContainer } from './styles.js';
import { ThemeProvider } from 'styled-components';
import { Link } from '../Link/Link.web.js';
import { GridContext } from '../Grid/Grid.web.js';
import { useSmartControlledToggle } from '../utils/hooks/useSmartControlledToggle.js';
import { useComponentVariant } from '../utils/hooks/useComponentVariant.js';
import { styles } from '../constants/styles.js';
import { renderComponentOrElement } from '../utils/helpers/component.js';
import { spacing } from '../constants/spacing.js';

var _excluded = ["opened", "backgroundColor", "textColor", "closeButtonAriaLabel", "hideCloseButton", "onClose", "children", "maxHeight", "contentFollowsGrid", "fullWidth", "numberOfLines", "readMoreText", "icon", "heading", "id", "headingProps", "iconButtonProps", "readMoreProps", "status", "variant"];
function isOverflown(element) {
  return element.scrollHeight > element.clientHeight;
}
var Notification = function Notification(_ref) {
  var openedProp = _ref.opened,
    backgroundColorProp = _ref.backgroundColor,
    textColorProp = _ref.textColor,
    _ref$closeButtonAriaL = _ref.closeButtonAriaLabel,
    closeButtonAriaLabel = _ref$closeButtonAriaL === undefined ? 'Close notification' : _ref$closeButtonAriaL,
    _ref$hideCloseButton = _ref.hideCloseButton,
    hideCloseButton = _ref$hideCloseButton === undefined ? false : _ref$hideCloseButton,
    onClose = _ref.onClose,
    children = _ref.children,
    maxHeight = _ref.maxHeight,
    _ref$contentFollowsGr = _ref.contentFollowsGrid,
    contentFollowsGrid = _ref$contentFollowsGr === undefined ? false : _ref$contentFollowsGr,
    _ref$fullWidth = _ref.fullWidth,
    fullWidth = _ref$fullWidth === undefined ? false : _ref$fullWidth,
    _ref$numberOfLines = _ref.numberOfLines,
    numberOfLines = _ref$numberOfLines === undefined ? {
      "default": 2,
      s: 1
    } : _ref$numberOfLines,
    _ref$readMoreText = _ref.readMoreText,
    readMoreText = _ref$readMoreText === undefined ? 'Read more' : _ref$readMoreText,
    icon = _ref.icon,
    heading = _ref.heading,
    id = _ref.id,
    headingProps = _ref.headingProps,
    iconButtonProps = _ref.iconButtonProps,
    readMoreProps = _ref.readMoreProps,
    _ref$status = _ref.status,
    status = _ref$status === undefined ? 'primary' : _ref$status,
    variantProp = _ref.variant,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useSmartControlledTo = useSmartControlledToggle(true, openedProp, onClose),
    _useSmartControlledTo2 = _slicedToArray(_useSmartControlledTo, 2),
    opened = _useSmartControlledTo2[0],
    handleToggle = _useSmartControlledTo2[1];
  var tabIndex = opened ? undefined : -1;
  var _React$useContext = React.useContext(GridContext),
    offsets = _React$useContext.offsets;
  var theme = useTheme();
  var rawTheme = theme.notification.web;
  var container = React.useRef(null);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isExpanded = _React$useState2[0],
    setIsExpanded = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isOverflowing = _React$useState4[0],
    setIsOverflowing = _React$useState4[1];
  var variant = variantProp || theme.variant;
  var notificationTheme = useComponentVariant(rawTheme, variant);
  React.useLayoutEffect(function () {
    // This effect needs to run when content is changed or the notification is opened
    if (container.current) {
      var el = container.current;
      if (opened && isOverflown(el)) {
        setIsOverflowing(true);
      }
    }
  }, [container, children, heading, opened, setIsOverflowing]);
  var _notificationTheme$co = notificationTheme.colors[status],
    themeTextColor = _notificationTheme$co.textColor,
    themeBackground = _notificationTheme$co.background;
  var backgroundColor = backgroundColorProp !== null && backgroundColorProp !== undefined ? backgroundColorProp : themeBackground;
  var textColor = textColorProp !== null && textColorProp !== undefined ? textColorProp : themeTextColor;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Box, {
    "data-testid": "elemental-Notification",
    "data-opened": opened,
    transition: "max-height ".concat(styles.transition.timing.slowest),
    transitionTimingFunction: styles.transition["function"].base,
    overflowY: "hidden",
    maxHeight: opened ? isExpanded ? 'unset' : maxHeight : 0,
    id: id
  }, /*#__PURE__*/React.createElement(outerContainer, _extends({
    maxHeight: opened ? isExpanded ? 'unset' : maxHeight : 0,
    borderRadius: fullWidth ? undefined : notificationTheme.borderRadius,
    $offsets: offsets,
    $contentFollowsGrid: contentFollowsGrid,
    textColor: textColor,
    backgroundColor: backgroundColor
  }, props), /*#__PURE__*/React.createElement(contentWrapper, {
    $textColor: textColor
  }, icon && renderComponentOrElement(icon, {
    size: 24,
    mr: spacing.xxxs
  }), opened ? /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    role: "alert",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0
  }, heading && typeof heading === 'string' ? /*#__PURE__*/React.createElement(ContentText // Notification heading
  , _extends({
    "data-testid": "elemental-Notification-Heading",
    textColor: textColor,
    textAlign: "left",
    maxWidth: "100%",
    flex: 1,
    lineHeight: notificationTheme.heading.lineHeight,
    weight: notificationTheme.heading.weight,
    mb: notificationTheme.heading.marginBottom
  }, headingProps), heading) : heading, /*#__PURE__*/React.createElement(textContainer, {
    "data-testid": "elemental-Notification-TextContainer",
    $isExpanded: isExpanded,
    $numberOfLines: numberOfLines,
    ref: container
  }, children), isOverflowing && !isExpanded ? /*#__PURE__*/React.createElement(Link, _extends({
    onClick: function onClick(event) {
      event.preventDefault();
      setIsExpanded(true);
    }
  }, readMoreProps, {
    href: "#",
    tabIndex: 0,
    disableVisited: true,
    color: textColor
  }), readMoreText) : undefined) :
  /*#__PURE__*/
  /**
   * This element needs to be different than that of above, otherwise screen readers won't
   * read the appearing alert.
   */
  React.createElement("span", {
    style: {
      flex: 1
    }
  }, children), !hideCloseButton && /*#__PURE__*/React.createElement(IconButton, _extends({
    icon: IconCross,
    color: backgroundColor,
    textColor: textColor,
    onClick: handleToggle,
    tabIndex: tabIndex,
    "data-testid": "elemental-Notification-close",
    "aria-label": closeButtonAriaLabel
    // Allow slight overflow into surrounding padding so the icon is in
    // line with content
    ,
    m: "-".concat(spacing.xxxs)
    // Don't however let content overlap with the IconButton
    ,
    ml: spacing.xxxs
  }, iconButtonProps))))));
};

export { Notification };
